import { useCallback, useEffect, useRef, useState } from 'react';

import { MfaMethod, type MfaVerification, type MfaVerificationProof } from '@/api/mfa/types';

import { getMfaErrorCode, getMfaErrorMessage, parseMfaVerificationRequired } from './mfa-errors';
import { startOwnedAuthentication } from './webauthn-ceremony';

export enum MfaVerificationPhase {
  Closed = 'closed',
  Choose = 'choose',
  Totp = 'totp',
  Pending = 'pending',
  Error = 'error',
}

type ProtectedOperation<T> = (_verification?: MfaVerificationProof) => Promise<T>;

type PendingOperation = {
  request: MfaVerification;
  operation: ProtectedOperation<unknown>;
  resolve: (_value: unknown) => void;
  reject: (_reason: unknown) => void;
};

export type MfaVerificationState = {
  phase: MfaVerificationPhase;
  request: MfaVerification | null;
  code: string;
  error: string | null;
  retryMethod: MfaMethod | null;
};

const closedState = (): MfaVerificationState => ({
  phase: MfaVerificationPhase.Closed,
  request: null,
  code: '',
  error: null,
  retryMethod: null,
});

const RETRYABLE_PROOF_CODES = new Set([
  'USER.MFA.VERIFICATION_FAILED',
  'USER.MFA.TOTP_STEP_REPLAYED',
  'USER.MFA.PASSKEY_COUNTER_REPLAYED',
]);
const PASSKEY_CANCELLATION_NAMES = new Set(['AbortError', 'NotAllowedError']);

const proofCanBeRetried = (error: unknown): boolean =>
  RETRYABLE_PROOF_CODES.has(getMfaErrorCode(error) ?? '');

const isPasskeyCancellation = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('name' in error)) {
    return false;
  }
  return PASSKEY_CANCELLATION_NAMES.has(String((error as { name: unknown }).name));
};

const verificationState = (request: MfaVerification): MfaVerificationState => {
  let phase = MfaVerificationPhase.Pending;
  if (request.allowedMethods.length > 1) {
    phase = MfaVerificationPhase.Choose;
  } else if (request.allowedMethods[0] === MfaMethod.Totp) {
    phase = MfaVerificationPhase.Totp;
  }
  return { phase, request, code: '', error: null, retryMethod: null };
};

export class MfaVerificationCancelledError extends Error {
  constructor() {
    super('MFA verification was cancelled.');
    this.name = 'MfaVerificationCancelledError';
  }
}

export type MfaVerificationController = {
  state: MfaVerificationState;
  runProtected: <T>(_operation: ProtectedOperation<T>) => Promise<T>;
  chooseMethod: (_method: MfaMethod) => void;
  setCode: (_code: string) => void;
  retry: () => void;
  cancel: () => void;
};

export const useMfaVerification = (): MfaVerificationController => {
  const [state, setState] = useState<MfaVerificationState>(closedState);
  const activeRef = useRef<PendingOperation | null>(null);
  const attemptRef = useRef(0);
  const runningAttemptRef = useRef<number | null>(null);
  const expiryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ceremonyAttemptRef = useRef<{ attempt: number; cancel: () => void } | null>(null);

  const clearExpiry = useCallback(() => {
    if (expiryRef.current !== null) {
      clearTimeout(expiryRef.current);
    }
    expiryRef.current = null;
  }, []);

  const cancelOwnedCeremony = useCallback((attempt: number) => {
    if (ceremonyAttemptRef.current?.attempt !== attempt) {
      return;
    }
    ceremonyAttemptRef.current.cancel();
    ceremonyAttemptRef.current = null;
  }, []);

  const releaseAttempt = useCallback(
    (attempt: number) => {
      clearExpiry();
      cancelOwnedCeremony(attempt);
      attemptRef.current += 1;
      if (runningAttemptRef.current === attempt) {
        runningAttemptRef.current = null;
      }
      activeRef.current = null;
    },
    [cancelOwnedCeremony, clearExpiry],
  );

  const close = useCallback(
    (attempt: number) => {
      releaseAttempt(attempt);
      setState(closedState());
    },
    [releaseAttempt],
  );

  const keepOpen = useCallback((attempt: number, error: unknown, retryMethod: MfaMethod | null) => {
    if (attemptRef.current !== attempt) {
      return;
    }
    let phase = MfaVerificationPhase.Error;
    if (retryMethod === MfaMethod.Totp) {
      phase = MfaVerificationPhase.Totp;
    }
    setState((current) => ({
      ...current,
      phase,
      code: '',
      error: getMfaErrorMessage(error),
      retryMethod,
    }));
  }, []);

  const rejectAndClose = useCallback(
    (attempt: number, error: unknown) => {
      if (attemptRef.current !== attempt) {
        return;
      }
      const active = activeRef.current;
      active?.reject(error);
      close(attempt);
    },
    [close],
  );

  const submitProof = useCallback(
    async (attempt: number, proof: MfaVerificationProof) => {
      const active = activeRef.current;
      if (!active || attemptRef.current !== attempt) {
        return;
      }
      try {
        const result = await active.operation(proof);
        if (attemptRef.current !== attempt) {
          return;
        }
        active.resolve(result);
        close(attempt);
      } catch (error) {
        if (attemptRef.current !== attempt) {
          return;
        }
        if (proofCanBeRetried(error)) {
          keepOpen(attempt, error, proof.method);
        } else {
          rejectAndClose(attempt, error);
        }
      }
    },
    [close, keepOpen, rejectAndClose],
  );

  const getPasskeyAssertion = useCallback(async (attempt: number, request: MfaVerification) => {
    if (!request.publicKey) {
      throw new Error('Passkey verification is unavailable.');
    }
    const ceremony = startOwnedAuthentication({ optionsJSON: request.publicKey });
    ceremonyAttemptRef.current = { attempt, cancel: ceremony.cancel };
    const assertion = await ceremony.result;
    if (attemptRef.current !== attempt) {
      throw new MfaVerificationCancelledError();
    }
    ceremonyAttemptRef.current = null;
    return assertion;
  }, []);

  const confirmPasskey = useCallback(
    async (attempt: number, request: MfaVerification) => {
      setState((current) => ({ ...current, phase: MfaVerificationPhase.Pending, error: null }));
      try {
        const assertion = await getPasskeyAssertion(attempt, request);
        await submitProof(attempt, {
          method: MfaMethod.Passkey,
          interactionToken: request.interactionToken,
          assertion,
        });
      } catch (error) {
        if (attemptRef.current !== attempt) {
          return;
        }
        ceremonyAttemptRef.current = null;
        let normalized = error;
        if (isPasskeyCancellation(error)) {
          normalized = Object.assign(new Error('Confirmation was cancelled.'), {
            name: 'PasskeyCancelled',
          });
          keepOpen(attempt, normalized, MfaMethod.Passkey);
        } else {
          rejectAndClose(attempt, normalized);
        }
      }
    },
    [getPasskeyAssertion, keepOpen, rejectAndClose, submitProof],
  );

  const showTotp = useCallback(() => {
    setState((current) => ({
      ...current,
      phase: MfaVerificationPhase.Totp,
      code: '',
      error: null,
      retryMethod: null,
    }));
  }, []);

  const chooseMethod = useCallback(
    (method: MfaMethod) => {
      const active = activeRef.current;
      if (!active || !active.request.allowedMethods.includes(method)) {
        return;
      }
      const attempt = attemptRef.current;
      if (method === MfaMethod.Passkey) {
        void confirmPasskey(attempt, active.request);
      } else {
        showTotp();
      }
    },
    [confirmPasskey, showTotp],
  );

  const submitTotp = useCallback(
    async (attempt: number, request: MfaVerification, code: string) => {
      setState((current) => ({ ...current, phase: MfaVerificationPhase.Pending }));
      await submitProof(attempt, {
        method: MfaMethod.Totp,
        interactionToken: request.interactionToken,
        code,
      });
    },
    [submitProof],
  );

  const setCode = useCallback(
    (code: string) => {
      if (!/^\d{0,6}$/.test(code)) {
        return;
      }
      setState((current) => ({ ...current, code, error: null }));
      if (code.length !== 6) {
        return;
      }
      const active = activeRef.current;
      const attempt = attemptRef.current;
      if (!active) {
        return;
      }
      void submitTotp(attempt, active.request, code);
    },
    [submitTotp],
  );

  const cancel = useCallback(() => {
    const attempt = attemptRef.current;
    const active = activeRef.current;
    active?.reject(new MfaVerificationCancelledError());
    close(attempt);
  }, [close]);

  const retry = useCallback(() => {
    const method = state.retryMethod;
    if (method) {
      chooseMethod(method);
      return;
    }
    cancel();
  }, [cancel, chooseMethod, state.retryMethod]);

  const expire = useCallback(
    (attempt: number) => {
      if (attemptRef.current !== attempt) {
        return;
      }
      const active = activeRef.current;
      releaseAttempt(attempt);
      active?.reject(new Error('MFA verification expired.'));
      setState({
        phase: MfaVerificationPhase.Error,
        request: null,
        code: '',
        error: 'This request expired. Restart the MFA action to try again.',
        retryMethod: null,
      });
    },
    [releaseAttempt],
  );

  const waitForVerification = useCallback(
    <T>(attempt: number, request: MfaVerification, operation: ProtectedOperation<T>): Promise<T> =>
      new Promise<T>((resolve, reject) => {
        activeRef.current = {
          request,
          operation: operation as ProtectedOperation<unknown>,
          resolve: resolve as (_value: unknown) => void,
          reject,
        };
        const expiresAt = Date.parse(request.expiresAt);
        const expiresIn = Number.isFinite(expiresAt) ? Math.max(0, expiresAt - Date.now()) : 0;
        expiryRef.current = setTimeout(() => expire(attempt), Math.min(expiresIn, 2_147_483_647));
        setState(verificationState(request));
        if (
          request.allowedMethods.length === 1 &&
          request.allowedMethods[0] === MfaMethod.Passkey
        ) {
          void confirmPasskey(attempt, request);
        }
      }),
    [confirmPasskey, expire],
  );

  const runProtected = useCallback(
    async <T>(operation: ProtectedOperation<T>): Promise<T> => {
      if (runningAttemptRef.current !== null) {
        throw new Error('Another MFA verification is already active.');
      }
      const invocation = attemptRef.current + 1;
      attemptRef.current = invocation;
      runningAttemptRef.current = invocation;
      try {
        const result = await operation(undefined);
        if (attemptRef.current !== invocation) {
          throw new MfaVerificationCancelledError();
        }
        runningAttemptRef.current = null;
        return result;
      } catch (error) {
        if (attemptRef.current !== invocation) {
          throw new MfaVerificationCancelledError();
        }
        const request = parseMfaVerificationRequired(error);
        if (!request) {
          runningAttemptRef.current = null;
          throw error;
        }
        return waitForVerification(invocation, request, operation);
      }
    },
    [waitForVerification],
  );

  useEffect(
    () => () => {
      const attempt = attemptRef.current;
      const active = activeRef.current;
      releaseAttempt(attempt);
      active?.reject(new MfaVerificationCancelledError());
    },
    [releaseAttempt],
  );

  return { state, runProtected, chooseMethod, setCode, retry, cancel };
};
